import { useState, useEffect, useCallback } from "react";
import {
  FaSearch,
  FaSyncAlt,
  FaInbox,
  FaCalendarAlt,
  FaEdit,
  FaTimes,
} from "react-icons/fa";
import { doc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "../../config/firebase";
import adminCalendarService from "../../services/adminCalendarService";

const STATUS_BADGE = {
  pending: "bg-amber-100  text-amber-800  ring-1 ring-amber-200",
  confirmed: "bg-blue-100   text-blue-800   ring-1 ring-blue-200",
  completed: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  cancelled: "bg-red-100    text-red-800    ring-1 ring-red-200",
};

const STATUSES = ["all", "pending", "confirmed", "completed", "cancelled"];

const EXTRAS_NAMES = {
  water: "Water",
  softdrink: "Soft Drink",
  beer: "Beer",
  champagne: "Champagne",
  "seafood-salad": "Seafood Salad",
  "mix-crudo": "Mix Crudo",
  "linguine-seafood": "Linguine Seafood",
  "risotto-seafood": "Risotto Seafood",
  "seafood-fried": "Seafood Fried",
  "seafood-grilled": "Seafood Grilled",
  "chocolate-souffle": "Chocolate Soufflé",
};

const EXTRAS_CATS = {
  drinks: [
    { id: "water", name: "Water", price: 2 },
    { id: "softdrink", name: "Soft Drink", price: 3 },
    { id: "beer", name: "Beer", price: 5 },
    { id: "champagne", name: "Champagne", price: 30 },
  ],
  food: [
    { id: "seafood-salad", name: "Seafood Salad", price: 20 },
    { id: "mix-crudo", name: "Mix Crudo", price: 25 },
    { id: "linguine-seafood", name: "Linguine Seafood", price: 15 },
    { id: "risotto-seafood", name: "Risotto Seafood", price: 15 },
    { id: "seafood-fried", name: "Seafood Fried", price: 20 },
    { id: "seafood-grilled", name: "Seafood Grilled", price: 25 },
    { id: "chocolate-souffle", name: "Chocolate Soufflé", price: 5 },
  ],
};

const EXTRAS_PRICES = Object.fromEntries(
  [...EXTRAS_CATS.drinks, ...EXTRAS_CATS.food].map(({ id, price }) => [
    id,
    price,
  ]),
);

function computeExtrasTotal(extras) {
  if (!extras) return 0;
  const all = { ...extras.drinks, ...extras.food };
  return (
    Object.entries(all).reduce(
      (sum, [id, qty]) => sum + (EXTRAS_PRICES[id] || 0) * Number(qty || 0),
      0,
    ) + (extras.jetSki ? 50 : 0)
  );
}

function formatExtras(extras) {
  if (!extras) return null;
  const all = { ...extras.drinks, ...extras.snacks, ...extras.food };
  const lines = Object.entries(all)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => `${EXTRAS_NAMES[id] || id} ×${qty}`);
  if (extras.jetSki === true) lines.push("Jet Ski on Board (15 min) \u00d71");
  return lines.length ? lines : null;
}

export default function AdminBookingsTable() {
  const [bookings, setBookings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // bookingId being acted on
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [msg, setMsg] = useState(null);
  const [page, setPage] = useState(0);
  const [editingBooking, setEditingBooking] = useState(null); // booking being edited
  const [editForm, setEditForm] = useState({});
  const PER_PAGE = 20;

  const notify = (text, type = "info") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminCalendarService.getAllBookings();
      setBookings(data);
    } catch (err) {
      notify("Error loading bookings", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let out = [...bookings];
    if (statusFilter !== "all")
      out = out.filter((b) => b.status === statusFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      out = out.filter(
        (b) =>
          b.bookingReference?.toLowerCase().includes(s) ||
          b.customerInfo?.email?.toLowerCase().includes(s) ||
          b.customerInfo?.name?.toLowerCase().includes(s) ||
          b.serviceType?.toLowerCase().includes(s) ||
          b.platform?.toLowerCase().includes(s) ||
          b.date?.includes(s),
      );
    }
    if (dateFilter) {
      out = out.filter((b) => b.date === dateFilter);
    }
    setFiltered(out);
    setPage(0);
  }, [bookings, statusFilter, search, dateFilter]);

  const openEdit = (b) => {
    setEditingBooking(b);
    const src = b.extras || {};
    const extrasFlat = {};
    EXTRAS_CATS.drinks.forEach((item) => {
      extrasFlat[item.id] = src.drinks?.[item.id] || 0;
    });
    EXTRAS_CATS.food.forEach((item) => {
      extrasFlat[item.id] = src.food?.[item.id] || 0;
    });
    const basePrice = (b.totalPrice ?? 0) - computeExtrasTotal(src);
    setEditForm({
      name: b.customerInfo?.name || "",
      email: b.customerInfo?.email || "",
      phone: b.customerInfo?.phone || "",
      date: b.date || "",
      timeSlot: b.timeSlot || "",
      numberOfGuests: b.numberOfGuests ?? "",
      totalPrice: b.totalPrice ?? "",
      notes: b.notes || "",
      extras: extrasFlat,
      jetSki: src.jetSki === true,
      basePrice,
    });
  };

  const handleEditSave = async () => {
    if (!editingBooking) return;
    setSaving(editingBooking.id);
    try {
      const ref = doc(db, "bookings", editingBooking.id);
      await updateDoc(ref, {
        "customerInfo.name": editForm.name,
        "customerInfo.email": editForm.email,
        "customerInfo.phone": editForm.phone,
        date: editForm.date,
        timeSlot: editForm.timeSlot,
        numberOfGuests: Number(editForm.numberOfGuests),
        totalPrice: Number(editForm.totalPrice),
        notes: editForm.notes,
        extras: {
          drinks: Object.fromEntries(
            EXTRAS_CATS.drinks.map((item) => [
              item.id,
              Number(editForm.extras?.[item.id] || 0),
            ]),
          ),
          food: Object.fromEntries(
            EXTRAS_CATS.food.map((item) => [
              item.id,
              Number(editForm.extras?.[item.id] || 0),
            ]),
          ),
          jetSki: editForm.jetSki === true,
        },
        updatedAt: Timestamp.now(),
      });
      notify("Booking updated successfully", "success");
      setEditingBooking(null);
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(null);
    }
  };

  const handleAction = async (bookingId, action) => {
    setSaving(bookingId);
    try {
      if (action === "confirm")
        await adminCalendarService.confirmBooking(bookingId);
      if (action === "complete")
        await adminCalendarService.completeBooking(bookingId);
      if (action === "cancel")
        await adminCalendarService.cancelBooking(bookingId);
      if (action === "delete") {
        if (!window.confirm("Permanently delete this booking?")) {
          setSaving(null);
          return;
        }
        await adminCalendarService.deleteBooking(bookingId);
      }
      notify("Booking updated successfully", "success");
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(null);
    }
  };

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageBookings = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-4">
      {/* Edit Modal */}
      {editingBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-bold text-slate-800 text-base">
                Edit Booking —{" "}
                <span className="font-mono text-sky-600">
                  {editingBooking.bookingReference}
                </span>
              </h3>
              <button
                onClick={() => setEditingBooking(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <FaTimes />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, date: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Time Slot
                  </label>
                  <input
                    type="text"
                    value={editForm.timeSlot}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, timeSlot: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Guests
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editForm.numberOfGuests}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        numberOfGuests: e.target.value,
                      }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Total Price (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.totalPrice}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, totalPrice: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Notes
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={3}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
                />
              </div>
              {/* Extras */}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                  Extras
                </p>
                {Object.entries(EXTRAS_CATS).map(([cat, items]) => (
                  <div key={cat} className="mb-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1 capitalize">
                      {cat}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {items.map((item) => (
                        <div
                          key={item.id}
                          className="flex flex-col items-start"
                        >
                          <span className="text-[11px] text-slate-500 mb-0.5">
                            {item.name}{" "}
                            <span className="text-slate-400">
                              €{item.price}
                            </span>
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            value={editForm.extras?.[item.id] || 0}
                            onChange={(e) =>
                              setEditForm((f) => {
                                const newExtras = {
                                  ...f.extras,
                                  [item.id]: Number(e.target.value),
                                };
                                const extTotal =
                                  Object.entries(newExtras).reduce(
                                    (s, [id, qty]) =>
                                      s + (EXTRAS_PRICES[id] || 0) * qty,
                                    0,
                                  ) + (f.jetSki ? 50 : 0);
                                return {
                                  ...f,
                                  extras: newExtras,
                                  totalPrice: (f.basePrice || 0) + extTotal,
                                };
                              })
                            }
                            className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-sky-400"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-1">
                  <input
                    id="edit-jetski"
                    type="checkbox"
                    checked={editForm.jetSki || false}
                    onChange={(e) =>
                      setEditForm((f) => {
                        const newJetSki = e.target.checked;
                        const extTotal =
                          Object.entries(f.extras || {}).reduce(
                            (s, [id, qty]) =>
                              s + (EXTRAS_PRICES[id] || 0) * qty,
                            0,
                          ) + (newJetSki ? 50 : 0);
                        return {
                          ...f,
                          jetSki: newJetSki,
                          totalPrice: (f.basePrice || 0) + extTotal,
                        };
                      })
                    }
                    className="w-4 h-4 rounded accent-sky-600"
                  />
                  <label
                    htmlFor="edit-jetski"
                    className="text-sm text-slate-600"
                  >
                    Jet Ski on Board (15 min) — €50
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setEditingBooking(null)}
                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={saving === editingBooking.id}
                className="flex-1 py-2 rounded-xl bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition disabled:opacity-50"
              >
                {saving === editingBooking.id ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {msg && (
        <div
          className={`px-4 py-2 rounded-lg text-sm font-medium border
          ${
            msg.type === "success"
              ? "bg-green-50 text-green-800 border-green-300"
              : msg.type === "error"
                ? "bg-red-50   text-red-800   border-red-300"
                : "bg-blue-50 text-blue-800 border-blue-300"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
        <div className="relative flex-1 sm:min-w-[200px] sm:max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none" />
          <input
            type="text"
            placeholder="Search name, email, reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-slate-200 rounded-xl pl-8 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-slate-50"
          />
        </div>
        <div className="relative">
          <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none" />
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-slate-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-slate-50 text-slate-700"
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
              title="Clear date filter"
            >
              ✕
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition capitalize
                ${
                  statusFilter === s
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="self-start sm:self-auto flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-200 transition"
        >
          <FaSyncAlt className="text-xs" /> Refresh
        </button>
      </div>

      <p className="text-xs text-gray-400">
        {filtered.length} booking{filtered.length !== 1 ? "s" : ""}
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">
          <FaSyncAlt className="animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-slate-300 gap-2">
          <FaInbox className="text-3xl" />
          <span className="text-sm">No bookings found.</span>
        </div>
      ) : (
        <>
          {/* ── MOBILE CARD VIEW ── */}
          <div className="sm:hidden space-y-3">
            {pageBookings.map((b) => (
              <div
                key={b.id}
                className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono font-bold text-gray-800 text-xs">
                      {b.bookingReference || b.id.slice(0, 8)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {b.date}
                      {b.timeSlot ? ` · ${b.timeSlot}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {b._isExternal && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200 capitalize">
                        {b.platform}
                      </span>
                    )}
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize flex-shrink-0 ${STATUS_BADGE[b.status] || STATUS_BADGE.pending}`}
                    >
                      {b.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {b.customerInfo?.name || "—"}
                    </p>
                    <p className="text-xs text-gray-400">
                      {b.customerInfo?.email || ""}
                    </p>
                    {b.customerInfo?.phone && (
                      <p className="text-xs text-gray-400">
                        {b.customerInfo.phone}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 capitalize">
                      {b.serviceType || b.serviceId || "—"}
                    </p>
                    <p className="text-xs text-gray-400">
                      {b.numberOfGuests} guest
                      {b.numberOfGuests !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-gray-800">
                      €{b.totalPrice}
                    </span>
                    {formatExtras(b.extras) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {formatExtras(b.extras).map((item) => (
                          <span
                            key={item}
                            className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatDate(b.createdAt)}
                  </span>
                </div>
                {saving === b.id ? (
                  <p className="text-xs text-gray-400">Saving…</p>
                ) : b._isExternal ? (
                  <p className="text-xs text-purple-500 pt-1 border-t">
                    Manage in External tab
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 pt-1 border-t">
                    <button
                      onClick={() => openEdit(b)}
                      className="flex-1 py-1.5 bg-slate-700 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 flex items-center justify-center gap-1"
                    >
                      <FaEdit className="text-[10px]" /> Edit
                    </button>
                    {b.status === "pending" && (
                      <button
                        onClick={() => handleAction(b.id, "confirm")}
                        className="flex-1 py-1.5 bg-blue-600 text-white rounded-xl text-xs font-semibold hover:bg-blue-700"
                      >
                        Confirm
                      </button>
                    )}
                    {(b.status === "pending" || b.status === "confirmed") && (
                      <button
                        onClick={() => handleAction(b.id, "complete")}
                        className="flex-1 py-1.5 bg-green-600 text-white rounded-xl text-xs font-semibold hover:bg-green-700"
                      >
                        Complete
                      </button>
                    )}
                    {b.status !== "cancelled" && b.status !== "completed" && (
                      <button
                        onClick={() => handleAction(b.id, "cancel")}
                        className="flex-1 py-1.5 bg-yellow-500 text-white rounded-xl text-xs font-semibold hover:bg-yellow-600"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={() => handleAction(b.id, "delete")}
                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded-xl text-xs font-semibold hover:bg-red-200"
                    >
                      Del
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── DESKTOP TABLE VIEW ── */}
          <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-slate-800 text-slate-200 text-xs uppercase tracking-wide">
                <tr>
                  {[
                    "Reference",
                    "Date",
                    "Service",
                    "Customer",
                    "Guests",
                    "Price",
                    "Status",
                    "Created",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageBookings.map((b) => (
                  <tr key={b.id} className="hover:bg-sky-50/40 transition">
                    <td className="px-4 py-3 font-mono font-medium text-slate-800 text-xs">
                      {b.bookingReference || b.id.slice(0, 8)}
                      {b._isExternal && (
                        <span className="ml-1 text-[10px] font-bold px-1 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200 capitalize">
                          {b.platform}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                      {b.date}
                      <br />
                      {b.timeSlot && (
                        <span className="text-xs text-slate-400">
                          {b.timeSlot}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-700">
                      {b.serviceType || b.serviceId || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800 font-medium">
                        {b.customerInfo?.name || "—"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {b.customerInfo?.email || ""}
                      </p>
                      {b.customerInfo?.phone && (
                        <p className="text-xs text-slate-400">
                          {b.customerInfo.phone}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-700">
                      {b.numberOfGuests}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      €{b.totalPrice}
                      {formatExtras(b.extras) && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {formatExtras(b.extras).map((item) => (
                            <span
                              key={item}
                              className="text-[10px] font-normal bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 block"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[b.status] || STATUS_BADGE.pending}`}
                      >
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {formatDate(b.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      {saving === b.id ? (
                        <span className="text-xs text-slate-400">Saving…</span>
                      ) : b._isExternal ? (
                        <span className="text-xs text-purple-500">
                          External tab
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => openEdit(b)}
                            className="px-2 py-1 bg-slate-700 text-white rounded-lg text-xs font-medium hover:bg-slate-800 transition flex items-center gap-1"
                          >
                            <FaEdit className="text-[9px]" /> Edit
                          </button>
                          {b.status === "pending" && (
                            <button
                              onClick={() => handleAction(b.id, "confirm")}
                              className="px-2 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
                            >
                              Confirm
                            </button>
                          )}
                          {(b.status === "pending" ||
                            b.status === "confirmed") && (
                            <button
                              onClick={() => handleAction(b.id, "complete")}
                              className="px-2 py-1 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition"
                            >
                              Done
                            </button>
                          )}
                          {b.status !== "cancelled" &&
                            b.status !== "completed" && (
                              <button
                                onClick={() => handleAction(b.id, "cancel")}
                                className="px-2 py-1 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition"
                              >
                                Cancel
                              </button>
                            )}
                          <button
                            onClick={() => handleAction(b.id, "delete")}
                            className="px-2 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition"
                          >
                            Del
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* end desktop table */}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 justify-center pt-3">
              <button
                onClick={() => setPage(0)}
                disabled={page === 0}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-sm disabled:opacity-30 hover:bg-slate-50 transition"
              >
                «
              </button>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-sm disabled:opacity-30 hover:bg-slate-50 transition"
              >
                ‹
              </button>
              <span className="text-sm text-slate-500 px-2">
                Page{" "}
                <span className="font-semibold text-slate-700">{page + 1}</span>{" "}
                / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-sm disabled:opacity-30 hover:bg-slate-50 transition"
              >
                ›
              </button>
              <button
                onClick={() => setPage(totalPages - 1)}
                disabled={page >= totalPages - 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-sm disabled:opacity-30 hover:bg-slate-50 transition"
              >
                »
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
